import { createFileRoute, useParams } from "@tanstack/react-router";

import { graphql } from "@repo/graphql-types";
import Clock from "@repo/icons/default/Clock";
import Left from "@repo/icons/default/Left";
import LocationMarker from "@repo/icons/default/LocationMarker";
import Question from "@repo/icons/default/Question";
import Warning from "@repo/icons/default/Warning";
import {
  CpsAlertV2Description,
  CpsAlertV2IconArea,
  CpsAlertV2Root,
  CpsAlertV2TextArea,
  CpsAlertV2Title,
  CpsBadge,
} from "corpus";

import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { HorariosFuncionamento } from "@/components/locations/horarios-funcionamento";
import {
  type DadosOcupacao,
  TabelaOcupacao,
} from "@/components/locations/tabela-ocupacao";
import { Page } from "@/components/ui/layout/page/page";
import { SelectableText } from "@/components/ui/selectable-text/selectable-text";
import { useGraphQL } from "@/hooks/use-graphql.ts";

const UnidadeDetalhesQuery = graphql(`
  query UnidadeDetalhes($codUnidade: Int!) {
    unidade: unidades_by_pk(codUnidade: $codUnidade) {
      nome
      observacoes
      ativo
      cep
      bairro
      numero
      logradouro
      complemento
      Cidade {
        nome
        UF {
          siglaUF
        }
      }
      unidadeHorariosFuncionamentos {
        diaSemana
        abertura
        fechamento
      }
    }
  }
`);

const UnidadeOcupacaoQuery = graphql(`
  query UnidadeOcupacaoQuery($codUnidade: Int!) {
    analyticsOcupacaoUnidade(arg1: { codUnidade: $codUnidade }) {
      data {
        diasemana
        hora
        ocupacao
      }
    }
  }
`);

const UnidadeDetalhesPage = (): React.JSX.Element => {
  const { locationId } = useParams({ from: "/locations/$locationId/" });
  const codUnidade = Number(locationId);

  const queryResult = useGraphQL(UnidadeDetalhesQuery, {
    codUnidade,
  });

  const unidade = queryResult.data?.unidade;

  const ocupacaoResult = useGraphQL(UnidadeOcupacaoQuery, { codUnidade });

  const ocupacao = ocupacaoResult.data?.analyticsOcupacaoUnidade?.data;

  if (!unidade) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">🏢</div>
        <p className="text-gray-600">Unidade não encontrada</p>
      </div>
    );
  }

  const enderecoCompleto = [
    unidade.logradouro,
    unidade.numero,
    unidade.bairro,
    unidade.Cidade.nome,
    unidade.Cidade.UF.siglaUF,
    unidade.cep,
  ]
    .filter(Boolean)
    .join(", ")
    .replace(
      /^(?<logradouro>[^,]+), (?<numero>\d+), (?<bairro>.+), (?<cidade>.+), (?<uf>[A-Z]{2}), (?<cep>.+)$/,
      "$<logradouro>, $<numero> - $<bairro>, $<cidade> - $<uf>, $<cep>",
    );

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Detalhes da Unidade" align="center" />
      </HeaderRoot>
      <Page>
        <div className="space-y-6">
          {/* Cabeçalho da unidade */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{unidade.nome}</h1>
            {!unidade.ativo && (
              <CpsBadge color="danger" variant="rounded" size="small" className="mb-2">
                Unidade Inativa
              </CpsBadge>
            )}

            {/* Endereço */}
            <div className="flex items-start space-x-3 mb-4">
              <LocationMarker size={24} className="fill-neutral-400 shrink-0 mt-1" />
              <div className="flex-1">
                <p className="text-gray-700">
                  <SelectableText>{enderecoCompleto}</SelectableText>
                </p>
                {Boolean(unidade.complemento) && (
                  <div className="mt-2">
                    <CpsBadge
                      color="neutral"
                      variant="rounded"
                      size="small"
                      className="text-xs"
                    >
                      {unidade.complemento}
                    </CpsBadge>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Horários de funcionamento */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center space-x-3 mb-4">
              <Clock size={24} className="fill-neutral-400" />
              <h2 className="text-lg font-semibold text-gray-900">
                Horários de Funcionamento
              </h2>
            </div>
            <HorariosFuncionamento horarios={unidade.unidadeHorariosFuncionamentos} />

            {/* Warning sobre feriados */}
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Warning size={20} className="fill-amber-600 shrink-0 mt-0.5" />
                <p className="text-amber-800 text-sm">
                  Para datas especiais e feriados, consulte o nosso{" "}
                  <a
                    href="https://ajuda.livance.com.br/calendario"
                    target="_blank"
                    className="underline hover:text-amber-900"
                    rel="noopener"
                  >
                    calendário oficial
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Tabela de Ocupação */}
          {ocupacao && Array.isArray(ocupacao) && ocupacao.length > 0 ? (
            <TabelaOcupacao dados={ocupacao as DadosOcupacao[]} />
          ) : null}

          {/* Observações */}
          <CpsAlertV2Root type="info">
            <CpsAlertV2IconArea>
              <Question className="fill-success-700 self-center" size={24} />
            </CpsAlertV2IconArea>
            <CpsAlertV2TextArea>
              <CpsAlertV2Title type="success">Observações</CpsAlertV2Title>
              <CpsAlertV2Description>{unidade.observacoes}</CpsAlertV2Description>
            </CpsAlertV2TextArea>
          </CpsAlertV2Root>
        </div>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/locations/$locationId/")({
  component: UnidadeDetalhesPage,
});
